import React, { useState, useRef, useEffect } from "react";
import { Volume2, VolumeX, Volume1 } from "lucide-react";

interface VolumeControlProps {
  volume: number;
  onVolumeChange: (volume: number) => void;
}

export function VolumeControl({ volume, onVolumeChange }: VolumeControlProps) {
  const [showSlider, setShowSlider] = useState(false);
  const [lastVolume, setLastVolume] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);
  const clickCountRef = useRef(0);

  // Handle click outside to close slider
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSlider(false);
      }
    };

    if (showSlider) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSlider]);

  const handleButtonClick = () => {
    clickCountRef.current += 1;

    // Clear existing timer
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    // Set timer to detect single vs double click
    clickTimerRef.current = setTimeout(() => {
      if (clickCountRef.current === 1) {
        // Single click: toggle slider
        setShowSlider(!showSlider);
      } else if (clickCountRef.current === 2) {
        // Double click: toggle mute
        if (volume > 0) {
          setLastVolume(volume);
          onVolumeChange(0);
        } else {
          onVolumeChange(lastVolume);
        }
      }
      clickCountRef.current = 0;
    }, 250);
  };

  const getVolumeIcon = () => {
    if (volume === 0) return <VolumeX className="w-5 h-5 text-white" />;
    if (volume < 0.5) return <Volume1 className="w-5 h-5 text-white" />;
    return <Volume2 className="w-5 h-5 text-white" />;
  };

  return (
    <div ref={containerRef} className="relative flex items-center">
      <button onClick={handleButtonClick} className={`p-2 rounded-full transition-all ${showSlider ? "bg-white/20" : "hover:bg-white/20"}`}>
        {getVolumeIcon()}
      </button>

      <div
        className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 md:bottom-auto md:left-full md:translate-x-0 md:top-1/2 md:-translate-y-1/2 md:ml-2 md:mb-0 flex items-center gap-2 bg-black/90 rounded-lg px-3 py-2 backdrop-blur-sm shadow-lg transition-all duration-300 ease-in-out whitespace-nowrap ${
          showSlider ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}>
        <style>{`
          .volume-slider-horizontal {
            -webkit-appearance: none;
            appearance: none;
            width: 80px;
            height: 4px;
            background: transparent;
            border-radius: 2px;
            outline: none;
            cursor: pointer;
          }
          .volume-slider-horizontal::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #a855f7;
            cursor: pointer;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
            margin-top: -4px;
          }
          .volume-slider-horizontal::-webkit-slider-thumb:hover {
            background: #9333ea;
          }
          .volume-slider-horizontal::-moz-range-thumb {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #a855f7;
            cursor: pointer;
            border: none;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
            transform: translateY(-4px);
          }
          .volume-slider-horizontal::-moz-range-thumb:hover {
            background: #9333ea;
          }
          .volume-slider-horizontal::-webkit-slider-runnable-track {
            height: 4px;
            background: linear-gradient(
              to right,
              #a855f7 0%,
              #a855f7 ${volume * 100}%,
              rgba(255, 255, 255, 0.3) ${volume * 100}%,
              rgba(255, 255, 255, 0.3) 100%
            );
            border-radius: 2px;
          }
          .volume-slider-horizontal::-moz-range-track {
            height: 4px;
            background: rgba(255, 255, 255, 0.3);
            border-radius: 2px;
          }
          .volume-slider-horizontal::-moz-range-progress {
            height: 4px;
            background: #a855f7;
            border-radius: 2px;
          }
        `}</style>

        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
          className="volume-slider-horizontal"
        />
        <span className="text-white text-xs font-medium min-w-[32px]">{Math.round(volume * 100)}%</span>
      </div>
    </div>
  );
}
